import { useCallback, useMemo, useRef, useState } from 'react'
import type { PosicionEquipoPublica, PosicionPublica } from '../datos/index.js'
import { conSigno, signoDe } from '../formato.js'
import { Bandera } from './Bandera.js'
import { Icono } from './Icono.js'
import { PodioFinal } from './Vitrina.js'

/**
 * La tabla del torneo, con su fila marcada.
 *
 * Es pública —el jugador ve a los 400, no solo a él— porque la pregunta que
 * viene después de «¿cómo voy?» es siempre «¿y el que va primero?». Lo que
 * cambia respecto de la tabla del organizador es que acá hay una fila que
 * importa más que las otras: la propia. Está resaltada y hay un botón que
 * lleva hasta ella, que con veinte filas parece de más y con cuatrocientas es
 * la diferencia entre encontrarse y rendirse.
 *
 * En el teléfono no hay ancho para las siete columnas de la app: los puntos a
 * favor —el cuarto criterio, el que casi nunca desempata— se esconden con la
 * hoja de estilo y vuelven en cuanto la pantalla los aguanta. Se esconden y no
 * se omiten a propósito: girar el teléfono los trae de vuelta sin recargar, y
 * quien discute su posición los necesita.
 *
 * En un torneo por equipos son DOS tablas. El jugador compite por su puesto y
 * por el del equipo al mismo tiempo, y el segundo no se puede deducir del
 * primero: es la suma de cuatro compañeros repartidos por toda la tabla. Se
 * conmuta con un selector y la de equipos arranca elegida, porque es la
 * clasificación de la modalidad y además contiene a la otra: cada fila muestra
 * a su gente con el puesto individual de cada uno.
 *
 * Y arranca desplegada. El equipo no es un dato en sí —es la suma de sus
 * jugadores— así que la pregunta que trae al jugador hasta acá («¿cómo vamos?»)
 * viene siempre pegada a la otra («¿y quién nos está sumando?»). Tenerla
 * cerrada obligaba a abrir equipo por equipo para leer lo mismo. Se puede
 * plegar el que estorba, y eso es lo que se recuerda: el estado guarda los
 * CERRADOS, no los abiertos, para que un equipo que aparece después entre
 * desplegado como todos.
 */

const METALES = ['oro', 'plata', 'bronce'] as const

type Metal = (typeof METALES)[number]

/** El metal que le toca a un puesto, o nada si no es podio. */
function metalDe(posicion: number, hayPodio: boolean): Metal | null {
  if (!hayPodio || posicion > METALES.length) return null
  return METALES[posicion - 1] ?? null
}

/** Cuál de las dos tablas se está mirando. */
type Vista = 'jugadores' | 'equipos'

export function TablaPosiciones({
  posiciones,
  equipos = [],
  /** Sin ninguna ronda cerrada están todos en cero y el orden es el del sorteo. */
  hayPodio,
}: {
  posiciones: PosicionPublica[]
  /** La tabla de equipos. Llega vacía fuera de la modalidad Equipos. */
  equipos?: PosicionEquipoPublica[]
  hayPodio: boolean
}) {
  const miFila = useRef<HTMLTableRowElement | null>(null)
  const porEquipos = equipos.length > 0
  const [vista, setVista] = useState<Vista>('equipos')
  /* Los equipos PLEGADOS: todos arrancan abiertos y acá se anotan los que el
     jugador cerró. */
  const [cerrados, setCerrados] = useState<ReadonlySet<string>>(new Set())

  const buscarme = useCallback(() => {
    miFila.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }, [])

  const alternar = useCallback((equipoId: string) => {
    setCerrados((previos) => {
      const siguiente = new Set(previos)
      if (!siguiente.delete(equipoId)) siguiente.add(equipoId)
      return siguiente
    })
  }, [])

  /* La gente de cada equipo, en el orden de la tabla individual: es la que llega
     ordenada, así que agrupar sin reordenar deja a cada equipo con los suyos del
     mejor al peor. */
  const porEquipo = useMemo(() => {
    const grupos = new Map<string, PosicionPublica[]>()
    for (const p of posiciones) {
      if (p.equipoId === null) continue
      const grupo = grupos.get(p.equipoId)
      if (grupo) grupo.push(p)
      else grupos.set(p.equipoId, [p])
    }
    return grupos
  }, [posiciones])

  const viendo: Vista = porEquipos ? vista : 'jugadores'
  const yo = posiciones.find((p) => p.esMio)
  const miEquipo = equipos.find((e) => e.esMio)
  const hayDesempates =
    viendo === 'equipos'
      ? equipos.some((e) => e.resueltoPorSorteo)
      : posiciones.some((p) => p.resueltoPorSorteo)

  return (
    <div className="tabla-envoltorio">
      {/* El selector arriba de todo: dice qué se está mirando, así que va antes
          de lo que se mira. En el teléfono los dos botones se reparten el ancho. */}
      {porEquipos && (
        <div className="tabla-vistas" role="group" aria-label="Qué tabla se muestra">
          <button
            className={viendo === 'equipos' ? 'activa' : ''}
            aria-pressed={viendo === 'equipos'}
            onClick={() => setVista('equipos')}
          >
            Equipos
          </button>
          <button
            className={viendo === 'jugadores' ? 'activa' : ''}
            aria-pressed={viendo === 'jugadores'}
            onClick={() => setVista('jugadores')}
          >
            Jugadores
          </button>
        </div>
      )}

      {/* Los tres de arriba en su escalón, antes de la tabla: es la foto del
          torneo y la primera pregunta de cualquiera que abre esta pestaña.
          Solo en la de jugadores, y solo con alguna ronda cerrada: antes el
          orden es el del sorteo y no hay podio que mostrar. */}
      {viendo === 'jugadores' && hayPodio && posiciones.length >= 3 && (
        <PodioFinal posiciones={posiciones} />
      )}

      {viendo === 'equipos'
        ? miEquipo && (
            <button className="boton-buscarme" onClick={buscarme}>
              <Icono nombre="flecha" tamano={16} />
              Ver mi equipo — {miEquipo.posicion}º de {equipos.length}
            </button>
          )
        : yo && (
            <button className="boton-buscarme" onClick={buscarme}>
              <Icono nombre="flecha" tamano={16} />
              Ver mi posición — {yo.posicion}º de {posiciones.length}
            </button>
          )}

      {viendo === 'equipos' ? (
        <table className="tabla tabla-equipos">
          <thead>
            <tr>
              <th className="col-puesto">#</th>
              <th>Equipo</th>
              <th className="num" title="Partidas ganadas por sus jugadores">
                G
              </th>
              <th className="num" title="Partidas perdidas por sus jugadores">
                P
              </th>
              <th className="num" title="Suma de la efectividad de sus jugadores">
                Efec.
              </th>
              <th className="num col-puntos" title="Puntos a favor, sin truncar al objetivo">
                Puntos
              </th>
            </tr>
          </thead>

          {equipos.map((eq) => {
            const metal = metalDe(eq.posicion, hayPodio)
            const abierto = !cerrados.has(eq.equipoId)
            const miembros = porEquipo.get(eq.equipoId) ?? []
            const clases = [eq.esMio ? 'mia' : '', metal ? `podio ${metal}` : '']
              .filter(Boolean)
              .join(' ')

            return (
              <tbody key={eq.equipoId} className={abierto ? 'equipo-abierto' : ''}>
                <tr className={clases} ref={eq.esMio ? miFila : undefined}>
                  <td className="col-puesto">
                    <Puesto
                      posicion={eq.posicion}
                      metal={metal}
                      resueltoPorSorteo={eq.resueltoPorSorteo}
                    />
                  </td>
                  <td className="col-nombre">
                    <div className="celda-nombre">
                      <button
                        className="equipo-desplegar"
                        aria-expanded={abierto}
                        onClick={() => alternar(eq.equipoId)}
                      >
                        <Icono nombre="flecha" tamano={14} />
                        <span className="acortar">{eq.nombre}</span>
                        {eq.esMio && <span className="silla-tu">tu equipo</span>}
                      </button>
                    </div>
                  </td>
                  <td className="num ganadas">{eq.ganadas}</td>
                  <td className="num perdidas">{eq.perdidas}</td>
                  <td className={`num efectividad ${signoDe(eq.efectividad)}`}>
                    {conSigno(eq.efectividad)}
                  </td>
                  <td className="num col-puntos">{eq.puntosFavor}</td>
                </tr>

                {abierto &&
                  miembros.map((p) => (
                    <tr
                      key={p.participanteId}
                      className={`fila-miembro ${p.esMio ? 'mia' : ''} ${
                        p.estado === 'retirado' ? 'retirado' : ''
                      }`}
                    >
                      <td className="col-puesto">
                        {/* El puesto de la tabla individual, no el orden dentro
                            del equipo: es el número que el jugador ya conoce. */}
                        <span className="puesto" title="Su puesto entre todos los jugadores">
                          {p.posicion}
                        </span>
                      </td>
                      <td className="col-nombre">
                        <div className="celda-nombre">
                          <Bandera codigo={p.pais} />
                          <span className="acortar">{p.nombre}</span>
                          {p.esMio && <span className="silla-tu">tú</span>}
                          <MarcaDeSalida posicion={p} />
                        </div>
                      </td>
                      <td className="num ganadas">{p.ganadas}</td>
                      <td className="num perdidas">{p.perdidas}</td>
                      <td className={`num efectividad ${signoDe(p.efectividad)}`}>
                        {conSigno(p.efectividad)}
                      </td>
                      <td className="num col-puntos">{p.puntosFavor}</td>
                    </tr>
                  ))}
              </tbody>
            )
          })}
        </table>
      ) : (
        <table className="tabla">
          <thead>
            <tr>
              <th className="col-puesto">#</th>
              <th>Jugador</th>
              <th className="num" title="Partidas ganadas">
                G
              </th>
              <th className="num" title="Partidas perdidas">
                P
              </th>
              <th className="num" title="Suma de la efectividad de cada partida">
                Efec.
              </th>
              <th className="num col-puntos" title="Puntos a favor, sin truncar al objetivo">
                Puntos
              </th>
            </tr>
          </thead>
          <tbody>
            {posiciones.map((p) => {
              const metal = metalDe(p.posicion, hayPodio)
              const clases = [
                p.esMio ? 'mia' : '',
                metal ? `podio ${metal}` : '',
                p.estado === 'retirado' ? 'retirado' : '',
              ]
                .filter(Boolean)
                .join(' ')

              return (
                <tr key={p.participanteId} className={clases} ref={p.esMio ? miFila : undefined}>
                  <td className="col-puesto">
                    <Puesto
                      posicion={p.posicion}
                      metal={metal}
                      resueltoPorSorteo={p.resueltoPorSorteo}
                    />
                  </td>
                  <td className="col-nombre">
                    <div className="celda-nombre">
                      <Bandera codigo={p.pais} />
                      <span className="acortar">{p.nombre}</span>
                      {p.esMio && <span className="silla-tu">tú</span>}
                      {/* El equipo al lado del nombre: sin esto, la tabla de
                          jugadores de un torneo por equipos no dice por quién
                          juega cada uno, que es la mitad de la pregunta. */}
                      {p.equipoNombre !== null && (
                        <span className="fila-equipo-nombre">{p.equipoNombre}</span>
                      )}
                      <MarcaDeSalida posicion={p} />
                    </div>
                  </td>
                  <td className="num ganadas">{p.ganadas}</td>
                  <td className="num perdidas">{p.perdidas}</td>
                  <td className={`num efectividad ${signoDe(p.efectividad)}`}>
                    {conSigno(p.efectividad)}
                  </td>
                  <td className="num col-puntos">{p.puntosFavor}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <p className="tabla-pie">
        {viendo === 'equipos'
          ? 'El puntaje del equipo es la suma de los de sus jugadores, y ordena por los mismos ' +
            'cuatro criterios. Toca un equipo para plegar o desplegar a su gente.'
          : 'Ordena por partidas ganadas, después por perdidas, después por efectividad y por ' +
            'último por puntos a favor.'}{' '}
        Lo que ni así se separa lo resuelve el sorteo del torneo
        {hayDesempates && (
          <>
            , marcado con <span className="marca-sorteo">~</span>
          </>
        )}
        .
      </p>
    </div>
  )
}

/** El puesto con su medalla si es podio, y la marca de sorteo si la lleva. */
function Puesto({
  posicion,
  metal,
  resueltoPorSorteo,
}: {
  posicion: number
  metal: Metal | null
  resueltoPorSorteo: boolean
}) {
  return (
    <>
      {metal ? (
        <span className="marca-podio" title={`Medalla de ${metal}`}>
          <Icono nombre="medalla" tamano={17} />
          <span>{posicion}</span>
        </span>
      ) : (
        <span className="puesto">{posicion}</span>
      )}
      {resueltoPorSorteo && (
        <span className="marca-sorteo" title="Su lugar frente al anterior lo definió el sorteo">
          ~
        </span>
      )}
    </>
  )
}

/**
 * Por qué alguien dejó de jugar, si dejó.
 *
 * El expulsado y el que se fue solo quedan los dos `retirado`, y en la tabla no
 * pueden verse igual: uno abandonó y al otro lo sacó el árbitro. Se dice qué
 * tarjeta fue y nada más; el motivo es del jugador y lo lee solo él.
 */
function MarcaDeSalida({ posicion }: { posicion: PosicionPublica }) {
  if (posicion.expulsion) {
    return (
      <span className="pastilla error" title={`Expulsado con tarjeta ${posicion.expulsion}`}>
        Expulsado
      </span>
    )
  }
  if (posicion.estado === 'retirado') return <span className="pastilla alerta">Retirado</span>
  return null
}
